import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FaRegCopy, FaCheck } from 'react-icons/fa';

// A cell wrapper that reveals a small copy button on hover.
// Use either `value` (renders as-is) or pass `children` for custom rendering;
// the copied text is always the `value` prop so composition stays predictable.
const CopyableCell = ({ value, children, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const handleCopy = useCallback(
    async (e) => {
      e.stopPropagation();
      e.preventDefault();
      const text = value === null || value === undefined ? '' : String(value);
      if (!text) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for non-secure contexts
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        setCopied(true);
        setFailed(false);
      } catch (err) {
        console.error('Copy failed:', err);
        setFailed(true);
      } finally {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setCopied(false);
          setFailed(false);
        }, 1200);
      }
    },
    [value]
  );

  const hasValue = value !== null && value !== undefined && value !== '';

  if (!hasValue) {
    return <span className="text-slate-400">—</span>;
  }

  return (
    <span className={`group/copy inline-flex items-center gap-1 max-w-full ${className}`}>
      <span className="truncate">{children ?? value}</span>
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? 'Copied' : failed ? 'Copy failed' : `Copy "${value}"`}
        aria-label={`Copy ${value}`}
        className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded opacity-0 group-hover/copy:opacity-100 focus:opacity-100 transition-opacity ${
          copied
            ? 'text-primary-600 opacity-100'
            : failed
              ? 'text-red-500 opacity-100'
              : 'text-slate-400 hover:text-primary-600 hover:bg-slate-100'
        }`}
      >
        {copied ? <FaCheck className="w-2.5 h-2.5" /> : <FaRegCopy className="w-2.5 h-2.5" />}
      </button>
    </span>
  );
};

export default CopyableCell;
